import { useState } from "react";
import { toast } from "sonner";
import { Plus, Phone, CalendarDays, Pencil } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { useAppState } from "@/context/AppState";

export default function Terapeutas() {
  const { therapists, appointments, addTherapist, updateTherapist } = useAppState();
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [name, setName] = useState("");
  const [role, setRole] = useState("");
  const [phone, setPhone] = useState("");

  const count = (id) => appointments.filter((a) => a.therapistId === id).length;

  const openNew = () => { setEditId(null); setName(""); setRole(""); setPhone(""); setOpen(true); };
  const openEdit = (t) => { setEditId(t.id); setName(t.name); setRole(t.role || ""); setPhone(t.phone || ""); setOpen(true); };

  const save = async () => {
    if (!name.trim()) return;
    const payload = {
      name: name.trim(),
      role: role.trim() || "Terapeuta",
      phone: phone.trim(),
      initials: name.trim().split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase(),
    };
    try {
      if (editId) {
        await updateTherapist(editId, payload);
        toast.success("Terapeuta actualizado.");
      } else {
        await addTherapist(payload);
        toast.success("Terapeuta añadido.");
      }
      setOpen(false);
    } catch (e) {
      toast.error("Algo no ha ido bien. Prueba otra vez o vuelve más tarde.");
    }
  };

  return (
    <div className="space-y-6 animate-fade-up" data-testid="terapeutas-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900">Terapeutas</h1>
          <p className="text-sm text-slate-500 mt-1">
            El equipo de tu consulta. Añade a quien trabaje contigo.
          </p>
        </div>
        <Button
          onClick={openNew}
          className="rounded-full text-white gap-2"
          style={{ backgroundColor: "#275FBE" }}
          data-testid="add-therapist-button"
        >
          <Plus className="h-4 w-4" />
          Añadir terapeuta
        </Button>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="therapists-list">
        {therapists.map((t) => (
          <div
            key={t.id}
            className="bg-white rounded-2xl border border-[#EAE5DF] p-5 shadow-[0_6px_24px_-12px_rgba(46,42,143,0.10)] hover:shadow-[0_14px_32px_-12px_rgba(46,42,143,0.18)] hover:-translate-y-0.5 transition-all duration-300"
            data-testid={`therapist-card-${t.id}`}
          >
            <div className="flex items-center gap-3">
              <span
                className="flex items-center justify-center h-11 w-11 rounded-full text-white text-sm font-semibold"
                style={{ backgroundColor: t.color }}
              >
                {t.initials}
              </span>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-slate-900 truncate">{t.name}</p>
                <p className="text-xs text-slate-500">{t.role}</p>
              </div>
              <button
                onClick={() => openEdit(t)}
                className="text-slate-400 hover:text-[#275FBE] transition-colors p-1 shrink-0"
                title="Editar terapeuta"
                data-testid={`edit-therapist-${t.id}`}
              >
                <Pencil className="h-4 w-4" />
              </button>
            </div>
            <div className="mt-4 pt-3 border-t border-slate-100 space-y-1 text-sm text-slate-500">
              <p className="inline-flex items-center gap-1.5">
                <Phone className="h-3.5 w-3.5" /> {t.phone || "Sin teléfono"}
              </p>
              <p className="inline-flex items-center gap-1.5">
                <CalendarDays className="h-3.5 w-3.5" /> {count(t.id)} citas
              </p>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-[420px] rounded-3xl" data-testid="therapist-modal">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-slate-900">
              {editId ? "Editar terapeuta" : "Añadir terapeuta"}
            </DialogTitle>
            <DialogDescription className="text-sm text-slate-500">
              {editId ? "Actualiza sus datos." : "Suma a un compañero de tu consulta."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label htmlFor="t-name">Nombre</Label>
              <Input id="t-name" value={name} onChange={(e) => setName(e.target.value)} data-testid="therapist-name" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="t-role">Especialidad</Label>
              <Input id="t-role" value={role} onChange={(e) => setRole(e.target.value)} placeholder="Psicóloga, coach..." data-testid="therapist-role" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="t-phone">Teléfono</Label>
              <Input id="t-phone" value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="therapist-phone" />
            </div>
          </div>
          <Button
            className="w-full rounded-full text-white"
            style={{ backgroundColor: "#275FBE" }}
            onClick={save}
            data-testid="save-therapist-button"
          >
            {editId ? "Guardar cambios" : "Guardar terapeuta"}
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
